import React from "react";
import useNCBTimeline from "./hooks/useNCBTimeline";

const NCBTimelineSection = () => {
  const {
    selectedPeriod,
    searchQuery,
    timelineData,
    ncbIndonesiaData,
    categories,
    setSelectedPeriod,
    setSearchQuery,
    getCategoryInfo,
  } = useNCBTimeline();

  const getSignificanceIcon = (significance) => {
    switch (significance) {
      case "high":
        return "🔴";
      case "medium":
        return "🟡";
      case "low":
        return "🟢";
      default:
        return "⚪";
    }
  };

  const getSignificanceLabel = (significance) => {
    switch (significance) {
      case "high":
        return "Sangat Penting";
      case "medium":
        return "Penting";
      case "low":
        return "Informasi";
      default:
        return "Normal";
    }
  };

  return (
    <>
      {/* Intro Section */}
      <div className="bg-neutral text-neutral-content rounded-2xl shadow-xl border border-neutral-300 p-8 m-8">
        <div className="flex items-start gap-x-4 mb-6">
          <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center flex-shrink-0">
            <span className="text-primary-content text-xl">ℹ️</span>
          </div>
          <div className="flex-1">
            <h2 className="text-2xl font-bold text-neutral-content mb-4">
              Latar Belakang Pembentukan ICPO-INTERPOL
            </h2>
            <div className="bg-neutral/20 border border-neutral-300 rounded-xl p-6">
              <p className="text-neutral-content/70 leading-relaxed mb-4">
                Lahirnya organisasi ICPO-INTERPOL karena adanya persamaan
                kepentingan dalam memberantas kejahatan transnasional dan
                internasional diantara negara-negara di dunia. Perkembangan
                teknologi mengakibatkan dampak yang signifikan dalam kehidupan
                internasional baik dampak positif maupun negatif berupa
                meningkatnya kejahatan internasional/transnasional.
              </p>
              <p className="text-neutral-content/70 leading-relaxed">
                Untuk mengatasi permasalahan tersebut, beberapa negara Eropa
                menyelenggarakan Kongres Polisi Reserse Internasional ke-1 di
                Monaco tahun 1914 dan menghasilkan kesepakatan untuk membentuk
                suatu Badan Kepolisian Internasional.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filter and Search Section */}
      <div className="bg-neutral text-neutral-content rounded-xl border border-neutral-300 p-6 mx-8 mb-8">
        <div className="flex flex-col laptop:flex-row gap-x-4 gap-y-4">
          {/* Search */}
          <div className="flex-1">
            <label className="block text-sm font-medium text-neutral-content mb-2">
              🔍 Cari dalam Timeline
            </label>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Cari berdasarkan judul, deskripsi, atau lokasi..."
              className="w-full px-4 py-2 border border-neutral-300 rounded-lg bg-neutral-content text-neutral focus:ring-2 focus:ring-primary focus:border-primary"
            />
          </div>

          {/* Category Filter */}
          <div className="laptop:w-64">
            <label className="block text-sm font-medium text-neutral-content mb-2">
              📂 Filter Kategori
            </label>
            <select
              value={selectedPeriod}
              onChange={(e) => setSelectedPeriod(e.target.value)}
              className="w-full px-4 py-2 border border-neutral-300 rounded-lg bg-neutral-content text-neutral focus:ring-2 focus:ring-primary focus:border-primary"
            >
              {categories.map((category) => (
                <option key={category.key} value={category.key}>
                  {category.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Results Summary */}
        <div className="mt-4 flex items-center justify-between">
          <p className="text-sm text-neutral-content/70">
            Menampilkan{" "}
            <span className="font-bold text-primary">
              {timelineData.length}
            </span>{" "}
            peristiwa
            {selectedPeriod !== "all" && (
              <span>
                {" "}
                dalam kategori{" "}
                <span className="font-bold text-primary">
                  {getCategoryInfo(selectedPeriod).label}
                </span>
              </span>
            )}
          </p>

          {(searchQuery || selectedPeriod !== "all") && (
            <button
              onClick={() => {
                setSearchQuery("");
                setSelectedPeriod("all");
              }}
              className="text-sm px-3 py-1 bg-primary text-primary-content rounded-lg hover:bg-primary-focus transition-colors"
            >
              Reset Filter
            </button>
          )}
        </div>
      </div>

      {/* Legend */}
      <div className="bg-neutral text-neutral-content rounded-xl border border-neutral-300 p-6 mx-8 mb-8">
        <h3 className="text-lg font-bold text-neutral-content mb-4">
          📊 Legenda Timeline
        </h3>
        <div className="grid grid-cols-1 laptop:grid-cols-2 gap-x-6 gap-y-4">
          <div>
            <h4 className="font-semibold text-neutral-content mb-2">
              Tingkat Kepentingan:
            </h4>
            <div className="space-y-2">
              <div className="flex items-center gap-x-2">
                <span>🔴</span>
                <span className="text-sm text-neutral-content/70">
                  Sangat Penting
                </span>
              </div>
              <div className="flex items-center gap-x-2">
                <span>🟡</span>
                <span className="text-sm text-neutral-content/70">Penting</span>
              </div>
              <div className="flex items-center gap-x-2">
                <span>🟢</span>
                <span className="text-sm text-neutral-content/70">
                  Informasi
                </span>
              </div>
            </div>
          </div>
          <div>
            <h4 className="font-semibold text-neutral-content mb-2">
              Kategori Peristiwa:
            </h4>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1">
              {categories.slice(1, 7).map((category) => (
                <div key={category.key} className="flex items-center gap-x-2">
                  <div
                    className={`w-3 h-3 bg-${category.color} rounded-full`}
                  ></div>
                  <span className="text-xs text-neutral-content/70">
                    {category.label}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Timeline */}
      <div className="bg-neutral text-neutral-content rounded-2xl shadow-xl border border-neutral-300 overflow-hidden mx-8 mb-8">
        <div className="bg-gradient-to-r from-primary to-primary-focus px-8 py-6">
          <h2 className="text-2xl font-bold text-primary-content flex items-center gap-x-3">
            <span>📚</span>
            Timeline Sejarah ICPO-INTERPOL
          </h2>
          <p className="text-primary-content/80 mt-1">
            Perkembangan organisasi dari tahun 1914 hingga sekarang
          </p>
        </div>

        <div className="p-8">
          {timelineData.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">🔍</div>
              <p className="text-neutral-content/70">
                Tidak ada peristiwa yang sesuai dengan filter yang dipilih.
              </p>
              <button
                onClick={() => {
                  setSearchQuery("");
                  setSelectedPeriod("all");
                }}
                className="mt-4 px-4 py-2 bg-primary text-primary-content rounded-lg hover:bg-primary-focus transition-colors"
              >
                Tampilkan Semua
              </button>
            </div>
          ) : (
            <div className="relative">
              {/* Continuous Timeline Line */}
              <div
                className="absolute left-1/2 transform -translate-x-1/2 w-1 bg-primary/40 hidden laptop:block"
                style={{ top: "60px", bottom: "60px" }}
              ></div>

              <div className="space-y-16">
                {timelineData.map((event, index) => {
                  const categoryInfo = getCategoryInfo(event.category);
                  const isEven = index % 2 === 0;

                  return (
                    <div key={`${event.year}-${index}`} className="relative">
                      {/* Timeline Layout */}
                      <div className="flex flex-col laptop:flex-row laptop:items-center">
                        {/* Left Content (for even items) or Empty Space (for odd items) */}
                        <div className="laptop:w-5/12 laptop:pr-8">
                          {isEven && (
                            <div className="bg-neutral-content text-neutral rounded-xl border border-neutral-300 p-6 shadow-md hover:shadow-lg transition-all duration-200 laptop:text-right">
                              {/* Header */}
                              <div className="flex items-center gap-x-3 mb-4 laptop:flex-row-reverse">
                                <div
                                  className={`px-3 py-1 bg-${categoryInfo.color} text-${categoryInfo.color}-content rounded-full text-xs font-medium`}
                                >
                                  {categoryInfo.label}
                                </div>
                                <div className="flex items-center gap-x-2">
                                  <span>
                                    {getSignificanceIcon(event.significance)}
                                  </span>
                                  <span className="text-xs text-neutral/60">
                                    {getSignificanceLabel(event.significance)}
                                  </span>
                                </div>
                              </div>

                              {/* Title */}
                              <h3 className="text-xl font-bold text-neutral mb-2">
                                {event.title}
                              </h3>

                              {/* Location */}
                              <div className="flex items-center gap-x-2 mb-3 text-sm text-neutral/60 laptop:justify-end">
                                <span>📍</span>
                                <span>{event.location}</span>
                              </div>

                              {/* Description */}
                              <p className="text-neutral/80 text-sm leading-relaxed">
                                {event.description}
                              </p>
                            </div>
                          )}
                        </div>

                        {/* Center Timeline Circle */}
                        <div className="laptop:w-2/12 flex justify-center my-4 laptop:my-0">
                          <div className="w-20 h-20 bg-primary rounded-full flex items-center justify-center shadow-lg z-20 relative border-4 border-neutral-content">
                            <span className="text-primary-content font-bold text-lg">
                              {event.year}
                            </span>
                          </div>
                        </div>

                        {/* Right Content (for odd items) or Empty Space (for even items) */}
                        <div className="laptop:w-5/12 laptop:pl-8">
                          {!isEven && (
                            <div className="bg-neutral-content text-neutral rounded-xl border border-neutral-300 p-6 shadow-md hover:shadow-lg transition-all duration-200">
                              {/* Header */}
                              <div className="flex items-center gap-x-3 mb-4">
                                <div
                                  className={`px-3 py-1 bg-${categoryInfo.color} text-${categoryInfo.color}-content rounded-full text-xs font-medium`}
                                >
                                  {categoryInfo.label}
                                </div>
                                <div className="flex items-center gap-x-2">
                                  <span>
                                    {getSignificanceIcon(event.significance)}
                                  </span>
                                  <span className="text-xs text-neutral/60">
                                    {getSignificanceLabel(event.significance)}
                                  </span>
                                </div>
                              </div>

                              {/* Title */}
                              <h3 className="text-xl font-bold text-neutral mb-2">
                                {event.title}
                              </h3>

                              {/* Location */}
                              <div className="flex items-center gap-x-2 mb-3 text-sm text-neutral/60">
                                <span>📍</span>
                                <span>{event.location}</span>
                              </div>

                              {/* Description */}
                              <p className="text-neutral/80 text-sm leading-relaxed">
                                {event.description}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Mobile Layout - Show content below circle on mobile */}
                      <div className="laptop:hidden mt-4">
                        <div className="bg-neutral-content text-neutral rounded-xl border border-neutral-300 p-6 shadow-md">
                          {/* Header */}
                          <div className="flex items-center gap-x-3 mb-4">
                            <div
                              className={`px-3 py-1 bg-${categoryInfo.color} text-${categoryInfo.color}-content rounded-full text-xs font-medium`}
                            >
                              {categoryInfo.label}
                            </div>
                            <div className="flex items-center gap-x-2">
                              <span>
                                {getSignificanceIcon(event.significance)}
                              </span>
                              <span className="text-xs text-neutral/60">
                                {getSignificanceLabel(event.significance)}
                              </span>
                            </div>
                          </div>

                          {/* Title */}
                          <h3 className="text-xl font-bold text-neutral mb-2">
                            {event.title}
                          </h3>

                          {/* Location */}
                          <div className="flex items-center gap-x-2 mb-3 text-sm text-neutral/60">
                            <span>📍</span>
                            <span>{event.location}</span>
                          </div>

                          {/* Description */}
                          <p className="text-neutral/80 text-sm leading-relaxed">
                            {event.description}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* NCB Indonesia Section */}
      <div className="mx-8 bg-gradient-to-r from-success to-success-focus rounded-2xl shadow-xl overflow-hidden">
        <div className="px-8 py-6 text-success-content">
          <h2 className="text-2xl font-bold flex items-center gap-x-3">
            <span>🇮🇩</span>
            NCB-INTERPOL Indonesia
          </h2>
          <p className="text-success-content/80 mt-1">
            Peran dan struktur organisasi di Indonesia
          </p>
        </div>

        <div className="bg-neutral text-neutral-content p-8">
          <div className="mb-8">
            <h3 className="text-xl font-bold text-neutral-content mb-4">
              Definisi dan Fungsi
            </h3>
            <p className="text-neutral-content/70 leading-relaxed bg-neutral/20 border border-neutral-300 rounded-lg p-4">
              {ncbIndonesiaData.definition}
            </p>
          </div>

          <div className="mb-8">
            <h3 className="text-xl font-bold text-neutral-content mb-4">
              Fungsi Utama
            </h3>
            <div className="grid grid-cols-1 laptop:grid-cols-2 gap-x-4 gap-y-3">
              {ncbIndonesiaData.functions.map((func, index) => (
                <div
                  key={index}
                  className="flex items-start gap-x-3 bg-neutral-content text-neutral border border-neutral-300 rounded-lg p-4"
                >
                  <div className="w-8 h-8 bg-success border-2 border-success rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-success-content text-sm font-bold">
                      {index + 1}
                    </span>
                  </div>
                  <p className="text-neutral/80 text-sm leading-relaxed">
                    {func}
                  </p>
                </div>
              ))}
            </div>
          </div>

          <div>
            <h3 className="text-xl font-bold text-neutral-content mb-4">
              Struktur Organisasi Saat Ini
            </h3>
            <div className="grid grid-cols-1 laptop:grid-cols-2 gap-x-6 gap-y-6">
              {ncbIndonesiaData.departments.map((dept, index) => (
                <div
                  key={index}
                  className="bg-neutral/20 border border-neutral-300 rounded-xl p-6"
                >
                  <h4 className="font-bold text-neutral-content text-lg mb-2">
                    {dept.name}
                  </h4>
                  {dept.oldName && (
                    <p className="text-sm text-neutral-content/60 mb-3">
                      Sebelumnya: {dept.oldName}
                    </p>
                  )}
                  <p className="text-neutral-content/70 text-sm leading-relaxed mb-4">
                    {dept.description}
                  </p>
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-neutral-content">
                      Sub Bagian:
                    </p>
                    <div className="flex flex-wrap gap-x-2 gap-y-1">
                      {dept.subDepartments.map((sub, subIndex) => (
                        <span
                          key={subIndex}
                          className="text-xs px-2 py-1 bg-primary text-primary-content rounded-full"
                        >
                          {sub}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default NCBTimelineSection;
